import { useMemo, useState } from "react"
import { Plus } from "lucide-react"
import { useTranslation } from "react-i18next"

import { StatusBadge } from "@/components/status-badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { MillFloorLoad } from "@/features/mill-floor/api/types"
import { useMillFloorLoads } from "@/features/mill-floor/hooks/use-mill-floor"
import {
  loadAvgBagKg,
  loadCooperativeName,
  loadRemainingBags,
  loadRemainingKg,
} from "@/features/mill-floor/lib/load-math"
import { usePatchUsinageLoads } from "@/features/milling/hooks/use-milling-mutations"
import { formatNumber, kgToMt } from "@/lib/format"

interface AddLoadsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  usinageId: string | number
  existingLoadIds: number[]
}

export function AddLoadsDialog({
  open,
  onOpenChange,
  usinageId,
  existingLoadIds,
}: AddLoadsDialogProps) {
  const { t } = useTranslation("milling")
  const { data: allLoads } = useMillFloorLoads("available")
  const patch = usePatchUsinageLoads(usinageId)
  const [assignments, setAssignments] = useState<Map<number, number>>(
    () => new Map(),
  )

  const existing = new Set(existingLoadIds)
  const candidates = (allLoads ?? []).filter((l) => !existing.has(l.id))

  const handleOpenChange = (next: boolean) => {
    if (!next) setAssignments(new Map())
    onOpenChange(next)
  }

  const summary = useMemo(() => {
    let bags = 0
    let kg = 0
    for (const [id, b] of assignments.entries()) {
      const l = candidates.find((x) => x.id === id)
      if (!l) continue
      bags += b
      kg += b * loadAvgBagKg(l)
    }
    return { bags, mt: kgToMt(kg) }
  }, [assignments, candidates])

  const canAdd = assignments.size > 0 && summary.bags > 0

  const submit = () => {
    if (!canAdd) return
    const add = Array.from(assignments.entries()).map(([loadId, bags]) => ({
      loadId,
      bags,
    }))
    patch.mutate(
      { add },
      { onSuccess: () => handleOpenChange(false) },
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("detail.report.addLoadTitle")}</DialogTitle>
          <DialogDescription>
            {t("detail.report.addLoadSubtitle")}
          </DialogDescription>
        </DialogHeader>

        {candidates.length === 0 ? (
          <div className="text-muted-foreground rounded-md border border-dashed p-8 text-center text-sm">
            {t("detail.report.noAvailableLoads")}
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-md border">
            {candidates.map((load, i) => {
              const assigned = assignments.get(load.id) ?? 0
              const checked = assignments.has(load.id)
              const remaining = loadRemainingBags(load)
              const partial = checked && assigned < remaining
              return (
                <Row
                  key={load.id}
                  load={load}
                  checked={checked}
                  partial={partial}
                  assigned={assigned}
                  isLast={i === candidates.length - 1}
                  onToggle={() => {
                    setAssignments((prev) => {
                      const next = new Map(prev)
                      if (next.has(load.id)) next.delete(load.id)
                      else next.set(load.id, remaining)
                      return next
                    })
                  }}
                  onChangeBags={(raw) => {
                    const n = Math.max(
                      0,
                      Math.min(remaining, Number(raw) || 0),
                    )
                    setAssignments((prev) => {
                      const next = new Map(prev)
                      if (n === 0) next.delete(load.id)
                      else next.set(load.id, n)
                      return next
                    })
                  }}
                />
              )
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={patch.isPending}
          >
            {t("detail.report.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!canAdd || patch.isPending}
          >
            <Plus className="size-4" />
            {t("detail.report.addSelected", {
              bags: formatNumber(summary.bags),
              mt: formatNumber(summary.mt, 2),
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Row({
  load,
  checked,
  partial,
  assigned,
  isLast,
  onToggle,
  onChangeBags,
}: {
  load: MillFloorLoad
  checked: boolean
  partial: boolean
  assigned: number
  isLast: boolean
  onToggle: () => void
  onChangeBags: (raw: string) => void
}) {
  const { t } = useTranslation("milling")
  const remaining = loadRemainingBags(load)
  const mt = kgToMt(loadRemainingKg(load))
  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 ${
        checked ? "bg-muted/40" : "hover:bg-muted/30"
      } ${isLast ? "" : "border-b"}`}
    >
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="w-24 truncate font-mono text-xs">
        {load.DeliveryForms?.billOfLadingNumber ?? `#${load.loadNumber}`}
      </span>
      {load.grade ? (
        <StatusBadge tone={load.grade === "GRADE_2" ? "warning" : "success"}>
          {load.grade.replace("_", " ")}
        </StatusBadge>
      ) : null}
      <span className="text-muted-foreground flex-1 truncate text-xs">
        {loadCooperativeName(load) ?? "—"}
      </span>
      <span className="text-muted-foreground w-36 text-right text-xs tabular-nums">
        {t("detail.report.bagsMt", {
          bags: formatNumber(remaining),
          mt: formatNumber(mt, 2),
        })}
      </span>
      {checked ? (
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min={0}
            max={remaining}
            value={assigned}
            onChange={(e) => onChangeBags(e.target.value)}
            className="w-20 text-right tabular-nums"
          />
          <span className="text-muted-foreground text-[10px]">
            / {remaining}
          </span>
          {partial ? (
            <StatusBadge tone="warning" className="text-[9px]">
              {t("detail.report.partialTag")}
            </StatusBadge>
          ) : null}
        </div>
      ) : (
        <div className="w-[170px]" aria-hidden />
      )}
    </div>
  )
}
